import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../api/client'
import { APP_NAME, APP_TAGLINE } from '../../constants'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          renderButton: (el: HTMLElement, config: Record<string, unknown>) => void
        }
      }
    }
  }
}

export function LoginScreen() {
  const login = useAuthStore((s) => s.login)
  const [mode, setMode] = useState<'choose' | 'phone' | 'otp'>('choose')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [devCode, setDevCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const googleBtnRef = useRef<HTMLDivElement>(null)
  const [googleReady, setGoogleReady] = useState<'loading' | 'ready' | 'failed'>('loading')

  const handleGoogleResponse = useCallback(async (response: { credential: string }) => {
    setLoading(true)
    setError('')
    try {
      const data = await api.post<{ token: string; user: { id: string; phone: string; email: string; name: string } }>(
        '/api/auth/google', { token: response.credential }
      )
      login(data.token, data.user)
    } catch (e: unknown) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [login])

  useEffect(() => {
    // Poll for Google GSI library to load (loaded async in index.html)
    let attempts = 0
    const maxAttempts = 25 // 5 seconds
    let buttonRendered = false

    const interval = setInterval(() => {
      attempts++
      if (buttonRendered) {
        clearInterval(interval)
        return
      }
      if (window.google?.accounts?.id && googleBtnRef.current) {
        clearInterval(interval)
        // Fetch client ID from backend
        api.get<{ client_id: string; configured: boolean }>('/api/auth/google-client-id').then((res) => {
          if (buttonRendered) return
          if (res.configured && res.client_id) {
            buttonRendered = true
            window.google!.accounts.id.initialize({
              client_id: res.client_id,
              callback: handleGoogleResponse,
            })
            // GSI renders into the pre-created div inside dangerouslySetInnerHTML
            const gsiDiv = googleBtnRef.current!.querySelector('#gsi-button') as HTMLElement
            if (!gsiDiv) return
            window.google!.accounts.id.renderButton(gsiDiv, {
              theme: 'outline',
              size: 'large',
              width: gsiDiv.offsetWidth || 300,
              text: 'signin_with',
            })
            setGoogleReady('ready')
          } else {
            setGoogleReady('failed')
          }
        }).catch(() => { setGoogleReady('failed') })
      } else if (attempts >= maxAttempts) {
        clearInterval(interval)
        setGoogleReady('failed')
      }
    }, 200)
    return () => clearInterval(interval)
  }, [handleGoogleResponse])

  const sendOTP = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await api.post<{ dev_code?: string }>('/api/auth/send-otp', { phone })
      if (data.dev_code) setDevCode(data.dev_code)
      setMode('otp')
    } catch (e: unknown) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const verifyOTP = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await api.post<{ token: string; user: { id: string; phone: string; email: string; name: string } }>(
        '/api/auth/verify-otp', { phone, code }
      )
      login(data.token, data.user)
    } catch (e: unknown) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-600 text-2xl font-bold text-white">
            S
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome to {APP_NAME}</h1>
          <p className="mt-2 text-sm font-light text-slate-400">{APP_TAGLINE}</p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-400">{error}</div>
        )}

        {mode === 'choose' && (
          <div className="space-y-4">
            {/* Google Sign-In — rendered by GSI library when available */}
            <div className="w-full flex items-center justify-center min-h-[44px]">
              {googleReady !== 'ready' ? (
                <span className="text-sm text-slate-500">
                  {googleReady === 'loading' ? 'Loading Google Sign-In...' : 'Google Sign-In unavailable'}
                </span>
              ) : null}
              <div
                ref={googleBtnRef}
                id="google-signin-container"
                style={{ display: googleReady === 'ready' ? 'block' : 'none' }}
                dangerouslySetInnerHTML={{ __html: '<div id="gsi-button"></div>' }}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700" /></div>
              <div className="relative flex justify-center text-sm"><span className="bg-slate-900 px-4 text-slate-500">or</span></div>
            </div>

            <button
              onClick={() => setMode('phone')}
              className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white hover:bg-indigo-500 transition-colors"
            >
              Sign in with Phone
            </button>
          </div>
        )}

        {mode === 'phone' && (
          <div className="space-y-4">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={sendOTP}
              disabled={loading || !phone}
              className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Code'}
            </button>
            <button onClick={() => setMode('choose')} className="w-full text-sm text-slate-400 hover:text-slate-200">Back</button>
          </div>
        )}

        {mode === 'otp' && (
          <div className="space-y-4">
            {devCode && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-400">
                Dev mode code: <span className="font-mono font-bold">{devCode}</span>
              </div>
            )}
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-center text-lg font-mono tracking-widest text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={verifyOTP}
              disabled={loading || code.length !== 6}
              className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button onClick={() => setMode('phone')} className="w-full text-sm text-slate-400 hover:text-slate-200">Back</button>
          </div>
        )}
      </div>
    </div>
  )
}
